import { customerQueryKey } from '@services/customer/keys';
import { quoteItemQueryKey } from '@services/quoteItem';
import { quoteFormQueryKeys } from '@services/quoteForm';
import { quoteQueryKey } from '@services/quotes/keys';
import { QueryClient, useQueryClient } from '@tanstack/react-query';

export const useInvalidateCustomerLocationsQuery = (customerId: string) => {
  const queryClient = useQueryClient();
  const invalidateQuery = () => {
    queryClient.invalidateQueries({
      queryKey: [...customerQueryKey._customerLocation(customerId), 'list'],
    });
    queryClient.invalidateQueries({
      queryKey: [...customerQueryKey._customerBrokerage(customerId), 'metadata'],
    });
  };
  return invalidateQuery;
};

/**
 * Shared by merge and split's onSuccess handlers: both customers changed (contacts and/or
 * quote lists, and for merge the loser's matchState) and any quote that moved between them
 * changed customer/status too.
 */
export const invalidateAfterCustomerCombine = (
  queryClient: QueryClient,
  customerIds: string[],
  movedQuoteIds: string[],
) =>
  Promise.all([
    ...customerIds.flatMap((customerId) => [
      queryClient.invalidateQueries({ queryKey: customerQueryKey.getCustomer(customerId) }),
      queryClient.invalidateQueries({ queryKey: customerQueryKey._customerContactInfo(customerId) }),
      queryClient.invalidateQueries({ queryKey: customerQueryKey.getBrokerageMetadata({ customerId }) }),
      // Customer page's quote count/list are keyed by customerId independently of the
      // per-quote keys below, so moved quotes leave them stale unless invalidated too.
      queryClient.invalidateQueries({ queryKey: ['quoteMetadata', { customerId }] }),
      queryClient.invalidateQueries({ queryKey: ['quoteSearch', { customerId }] }),
    ]),
    ...movedQuoteIds.flatMap((quoteId) => [
      queryClient.invalidateQueries({ queryKey: quoteQueryKey.get(quoteId) }),
      queryClient.invalidateQueries({ queryKey: quoteItemQueryKey.searchWithParams({ quoteId }) }),
      queryClient.invalidateQueries({ queryKey: quoteFormQueryKeys.get(quoteId) }),
    ]),
  ]);
